// Shared style system for the Funnel Builder + Public Funnel page.
// Block-level styles ALWAYS override; otherwise we fall back to theme.style (Site Styles).

export const SIZE_OPTIONS = {
  heading:   { sm: "text-2xl md:text-3xl", md: "text-3xl md:text-5xl", lg: "text-4xl md:text-6xl", xl: "text-5xl md:text-7xl", xxl: "text-6xl md:text-8xl" },
  paragraph: { sm: "text-sm",            md: "text-base",            lg: "text-lg",             xl: "text-xl md:text-2xl",  xxl: "text-2xl md:text-3xl" },
  benefits:  { sm: "text-sm",            md: "text-base",            lg: "text-lg",             xl: "text-xl",              xxl: "text-2xl" },
  quote:     { sm: "text-base",          md: "text-lg",              lg: "text-xl",             xl: "text-2xl",             xxl: "text-3xl" },
};

export const SIZE_LABELS = [
  { id: "sm",  label: "S"   },
  { id: "md",  label: "M"   },
  { id: "lg",  label: "L"   },
  { id: "xl",  label: "XL"  },
  { id: "xxl", label: "XXL" },
];

// 33 hand-picked colors organized in 6 families
export const COLOR_GROUPS = [
  { name: "Reds", colors: [
    { id: "red-1", label: "Soft Rose",    hex: "#E8B4B8" },
    { id: "red-2", label: "Coral",        hex: "#E8615C" },
    { id: "red-3", label: "Cardinal",     hex: "#C41E3A" },
    { id: "red-4", label: "Rich Red",     hex: "#2E9F3A" },
    { id: "red-5", label: "Burgundy",     hex: "#5C0A1F" },
    { id: "red-6", label: "Maroon",       hex: "#3D0814" },
  ]},
  { name: "Golds & Browns", colors: [
    { id: "gold-1", label: "Pale Gold",   hex: "#F4E4BC" },
    { id: "gold-2", label: "Honey",       hex: "#E5A823" },
    { id: "gold-3", label: "Burnished",   hex: "#F39A1F" },
    { id: "gold-4", label: "Bronze",      hex: "#8B6914" },
    { id: "gold-5", label: "Dark Bronze", hex: "#5C4416" },
    { id: "gold-6", label: "Coffee",      hex: "#3D2C14" },
  ]},
  { name: "Earth & Cream", colors: [
    { id: "earth-1", label: "Cream",      hex: "#FFFFFF" },
    { id: "earth-2", label: "Sand",       hex: "#D4C5A0" },
    { id: "earth-3", label: "Tan",        hex: "#A88860" },
    { id: "earth-4", label: "Walnut",     hex: "#7B5C3F" },
    { id: "earth-5", label: "Coffee",     hex: "#5C4633" },
    { id: "earth-6", label: "Espresso",   hex: "#2D1F12" },
  ]},
  { name: "Greens", colors: [
    { id: "green-1", label: "Mint",       hex: "#C8E6C9" },
    { id: "green-2", label: "Sage",       hex: "#9CAF88" },
    { id: "green-3", label: "Olive",      hex: "#6B7A4A" },
    { id: "green-4", label: "Forest",     hex: "#2D4A2B" },
    { id: "green-5", label: "Pine",       hex: "#1A2E18" },
  ]},
  { name: "Blues", colors: [
    { id: "blue-1", label: "Sky",         hex: "#A8C8E1" },
    { id: "blue-2", label: "Steel",       hex: "#5C7A99" },
    { id: "blue-3", label: "Royal",       hex: "#1E40AF" },
    { id: "blue-4", label: "Navy",        hex: "#1E2A47" },
    { id: "blue-5", label: "Midnight",    hex: "#0E1628" },
  ]},
  { name: "Neutrals", colors: [
    { id: "n-1", label: "White",          hex: "#FFFFFF" },
    { id: "n-2", label: "Light Gray",     hex: "#C8C8C8" },
    { id: "n-3", label: "Mid Gray",       hex: "#7A7A7A" },
    { id: "n-4", label: "Charcoal",       hex: "#2A2A2A" },
    { id: "n-5", label: "Black",          hex: "#000000" },
  ]},
];

export const COLOR_BY_ID = COLOR_GROUPS.flatMap(g => g.colors).reduce((a, c) => { a[c.id] = c; return a; }, {});

// 6 hand-curated font pairings (heading + body together)
export const FONT_PAIRS = [
  { id: "cormorant-manrope", name: "Faitharama Classic", heading: "'Cormorant Garamond', Georgia, serif", body: "'Manrope', system-ui, sans-serif", preview: "Aa Sunday Reflection" },
  { id: "playfair-lora",     name: "Old Pulpit",         heading: "'Playfair Display', Georgia, serif",   body: "'Lora', Georgia, serif",            preview: "Aa Old Pulpit" },
  { id: "anton-source",      name: "Bold Banner",        heading: "'Anton', Impact, sans-serif",          body: "'Source Sans 3', system-ui, sans-serif", preview: "Aa BOLD HEADLINES" },
  { id: "merriweather-opensans", name: "Newspaper",      heading: "'Merriweather', Georgia, serif",       body: "'Open Sans', system-ui, sans-serif", preview: "Aa Newspaper" },
  { id: "dmserif-inter",     name: "Modern Editorial",   heading: "'DM Serif Display', Georgia, serif",   body: "'Inter', system-ui, sans-serif",     preview: "Aa Editorial" },
  { id: "lora-merriweather", name: "Apologetics",       heading: "'Lora', Georgia, serif",               body: "'Merriweather', Georgia, serif",     preview: "Aa Apologetics" },
];

export const FONT_PAIR_BY_ID = FONT_PAIRS.reduce((a, p) => { a[p.id] = p; return a; }, {});

export const ALIGN_OPTIONS_DATA = [
  { id: "left",   label: "Left",   cls: "text-left"   },
  { id: "center", label: "Center", cls: "text-center" },
  { id: "right",  label: "Right",  cls: "text-right"  },
];

export const DEFAULT_COLOR_ID = {
  heading:   "red-4",
  paragraph: "earth-6",
  benefits:  "earth-6",
  quote:     "earth-6",
};

export const styleDefault = (type) => ({
  size:  type === "heading" ? "lg" : "md",
  color: DEFAULT_COLOR_ID[type] || "earth-6",
  align: "left",
});

export const themeStyleDefault = () => ({
  fontPair:     "cormorant-manrope",
  headingSize:  "lg",
  headingColor: "red-4",
  bodyColor:    "earth-6",
  bodySize:     "md",
  align:        "left",
});

/**
 * Resolve final style for a block.
 * Priority: block.style.<prop> overrides theme.style.<prop> overrides hard default.
 */
export const resolveStyle = (block, themeStyle) => {
  const t = themeStyle || themeStyleDefault();
  const s = block.style || {};
  const isHeading = block.type === "heading";
  const sizeId  = s.size  || (isHeading ? t.headingSize  : t.bodySize);
  const colorId = s.color || (isHeading ? t.headingColor : t.bodyColor);
  const alignId = s.align || t.align;
  const sizeMap = SIZE_OPTIONS[block.type] || SIZE_OPTIONS.paragraph;
  const sizeCls = sizeMap[sizeId] || sizeMap.md;
  const colorObj = COLOR_BY_ID[colorId] || COLOR_BY_ID["earth-6"];
  const alignCls = (ALIGN_OPTIONS_DATA.find(a => a.id === alignId) || ALIGN_OPTIONS_DATA[0]).cls;
  const pair = FONT_PAIR_BY_ID[t.fontPair] || FONT_PAIRS[0];
  const fontFamily = isHeading ? pair.heading : pair.body;
  return { sizeCls, alignCls, colorHex: colorObj.hex, fontFamily, raw: s };
};
